import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Styles
import styles from './style.module.scss';

// Components
import TextInput from '../../../../components/TextInput';
import Typography from '../../../../components/Typography';
import Segmented from '../../../../components/segmented';
import Button from '../../../../components/CustomButton';
import AttachMaterials from '../../../../components/AttachMaterials';
import SpeakersSelect from './SpeakersSelect';
import Select from '../../../../components/Select';
import ProcessingComponent from '../../../../components/ProcessingComponent';
import PlayerView from './PlayerView';

// Utils
import { generatePodcast } from '../../../../utils/generateMocks';

const Podcast: React.FC = () => {
  // Hooks
  const { t } = useTranslation();

  // States
  const [processing, setProcessing] = useState<boolean>(false);
  const [view, setView] = useState<'form' | 'player'>('form');

  const [title, setTitle] = useState<string>('');
  const [error, setError] = useState<{ title: boolean }>({ title: false });
  const [length, setLength] = useState(5);
  const [language, setLanguage] = useState('en');

  const [podcast, setPodcast] = useState<any>(null);

  // Memoized Values
  const lengthOptions = useMemo(() => {
    return ([
      { label: t('generate.podcast.length.5min'), value: 5 },
      { label: t('generate.podcast.length.10min'), value: 10 },
      { label: t('generate.podcast.length.15min'), value: 15 },
      { label: t('generate.podcast.length.30min'), value: 30 },
      { label: t('generate.podcast.length.60min'), value: 60 },
    ])
  }, [t]);

  const languageOptions = useMemo(() => {
    return ([
      { label: t('language.en'), value: 'en' },
      { label: t('language.es'), value: 'es' },
    ])
  }, [t]);

  // Handlers
  const handleSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setProcessing(true);
    if (!title) {
      setProcessing(false);
      setError(prev => ({ ...prev, title: true }));
      return;
    }

    await new Promise(resolve => setTimeout(resolve, 1000));
    setView('player');

    setPodcast(generatePodcast());
    setProcessing(false);

    // TODO: Generate podcast
  }, [title]);

  const handleChangeTitle = (value: string) => {
    setTitle(value);
    setError(prev => ({ ...prev, title: !value }));
  };
  const handleChangeLength = (value: any) => setLength(value as number);
  const handleChangeLanguage = (value: any) => setLanguage(value as string);

  if (processing) {
    return (
      <div className={styles.root}>
        <ProcessingComponent message={t('generate.podcast.processing_message')} />
      </div>
    )
  }

  if (view === 'player' && podcast) {
    return (
      <div className={styles.root}>
        <PlayerView podcast={podcast} onBack={() => setView('form')} />
      </div>
    )
  }

  return (
    <div className={styles.root}>
      <form className={styles.form} onSubmit={handleSubmit}>
        <div className={styles.form_container}>
          <div className={styles.form_inputs}>
            <TextInput
              label={t('generate.podcast.title_label')}
              value={title}
              onChange={handleChangeTitle}
              error={error.title ? t('generate.podcast.title_error') : ''}
              required
            />

            <div className={styles.form_item}>
              <Typography variant='p'>{t('generate.podcast.speakers_label')}</Typography>

              <SpeakersSelect />
            </div>

            <div className={styles.form_item}>
              <Typography variant='p'>{t('generate.podcast.length_label')}</Typography>

              <Segmented
                options={lengthOptions}
                value={length}
                onChange={handleChangeLength}
              />
            </div>

            <Select
              label={t('language.label')}
              options={languageOptions}
              value={language}
              onChange={handleChangeLanguage}
            />
          </div>

          <AttachMaterials />
        </div>

        <div className={styles.actions}>
          <Button type='submit' variant='primary' classes={{ root: styles.actions_button }}>
            {t('generate.start_generation')}
          </Button>
        </div>
      </form>
    </div>
  )
};

export default Podcast;
